import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { Button } from "ui/button";
import { StateNvr, createNvr } from "lib/actions";
import { claimEdgeCamera } from "pages/dashboard/customers/service";
import React, { useContext, useState } from "react";
import OTPInput from "ui/OTPInput/index";
import { useLocation } from "react-router-dom";
import { showToast } from "ui/components/common";
import { CameraInfo } from "lib/types";
import clsx from "clsx";

export default function Form({
  orgId,
  siteId,
}: {
  orgId: string;
  siteId: string;
}) {
  const { state } = useLocation();
  const [macAddress, setMacAddress] = useState("");
  const [serialNumber, setSerialNumber] = useState("");

  const [responseCamera, setResponseCamera] = useState<CameraInfo>();
  const [btnSubmitDisabled, setBtnSubmitDisabled] = useState(false);
  const [showMacAddressError, setShowMacAddressError] = useState(false);
  const [showSerialNumberError, setShowSerialNumberError] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();

    if (!macAddress) {
      setShowMacAddressError(true);
    } else {
      setShowMacAddressError(false);
    }
    if (!serialNumber) {
      setShowSerialNumberError(true);
    } else {
      setShowSerialNumberError(false);
    }
    if (!macAddress || !serialNumber) {
      return;
    }

    try {
      setBtnSubmitDisabled(true);
      const response = await claimEdgeCamera(
        +siteId,
        macAddress,
        serialNumber,
        orgId,
      );
      setBtnSubmitDisabled(false);
      if (response.status !== 200) {
        showToast(
          1,
          "There was an error claiming the camera. Please try again later.",
        );
        return;
      }
      setResponseCamera(response?.data?.cameras?.[0] as CameraInfo);
    } catch (error) {
      console.error(error);
    }
  }

  if (responseCamera) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="p-4 md:p-6">
          <h2 className="text-lg font-medium mb-5">
            Add Edge Camera to Site
            {" [" + state?.siteName + "]"}
          </h2>
          <div className="bg-gray-100 rounded-2xl p-4 md:p-6 grid grid-cols-2 gap-4 ">
            <div>
              Edge Camera Found
              <CheckCircleIcon className="inline-block ml-3 w-5 md:w-6 text-green-600" />
            </div>
            <div />
            <p className="text-slate-500">Camera Name</p>
            <p>{responseCamera.name}</p>
            <p className="text-slate-500">Model</p>
            <p>{responseCamera.model}</p>
            <p className="text-slate-500">MAC Address</p>
            <p>{responseCamera.macAddress}</p>
            <p className="text-slate-500">Serial Number</p>
            <p>{responseCamera.sn}</p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-4">
          <Link
            to="/dashboard/customers"
            className="flex items-center rounded-2xl bg-gray-100 px-6 py-4 text-base font-medium text-gray-600 transition-colors hover:bg-gray-200"
          >
            Cancel
          </Link>
          <Link
            to={`/dashboard/customers/${orgId}/sites/${siteId}`}
            className="flex items-center rounded-2xl bg-blue-500 px-6 py-4 text-base font-medium text-white transition-colors hover:bg-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 active:bg-blue-600 aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
          >
            Confirm
          </Link>
        </div>
      </div>
    );
  } else {
    return (
      <form onSubmit={handleSubmit} className="max-w-lg mx-auto">
        <div className="rounded-md">
          <h2 className="text-lg font-medium mb-5">
            Add Edge Camera to Site
            {" [" + state?.siteName + "]"}
          </h2>

          {/* Site Name */}
          <div className="mb-4">
            <label htmlFor="macAddress" className="mb-2 block text-sm">
              Last 6 digits of MAC Address
            </label>
            <div className="relative mt-2 rounded-md">
              <div className="relative">
                <OTPInput
                  autoFocus
                  length={6}
                  className="otpContainer"
                  inputClassName="otpInput w-12 h-12 mr-3 text-base text-center rounded-md border border-gray-200"
                  isMacAddressInput
                  onChangeOTP={(otp) =>
                    setMacAddress(otp.replace(/(..)/g, "$1:").slice(0, -1))
                  }
                />
                <input
                  id="macAddress"
                  name="macAddress"
                  type="hidden"
                  placeholder="Last 6 digits of MAC Address"
                  className="block w-full rounded-md border border-gray-200 py-2 text-sm outline-2 placeholder:text-gray-500"
                  aria-describedby="macAddress-error"
                  value={macAddress}
                />
              </div>
            </div>
            <p
              className={clsx(
                "text-sm text-red-600 ",
                showMacAddressError ? "visible" : "invisible",
              )}
            >
              Please enter Mac Address
            </p>
          </div>

          {/* Serial Number */}
          <div className="mb-4">
            <label htmlFor="serialNumber" className="mb-2 block text-sm">
              Last 6 characters of Serial Number
            </label>
            <div className="relative mt-2 rounded-md">
              <div className="relative">
                <OTPInput
                  autoFocus
                  length={6}
                  className="otpContainer"
                  inputClassName="otpInput w-12 h-12 mr-3 text-base text-center rounded-md border border-gray-200"
                  onChangeOTP={(otp) => setSerialNumber(otp)}
                />
                <input
                  id="serialNumber"
                  name="serialNumber"
                  type="hidden"
                  placeholder="Last 6 characters of Serial Number"
                  className="block w-full rounded-md border border-gray-200 py-2 text-sm outline-2 placeholder:text-gray-500"
                  aria-describedby="serialNumber-error"
                  value={serialNumber}
                />
              </div>
              <p
                className={clsx(
                  "text-sm text-red-600 ",
                  showSerialNumberError ? "visible" : "invisible",
                )}
              >
                Please enter Serial Number
              </p>
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-4">
          <Link
            to="/dashboard/customers"
            className="py-4 px-6 flex items-center rounded-2xl bg-gray-100 text-BASE font-medium text-gray-600 transition-colors hover:bg-gray-200"
          >
            Cancel
          </Link>
          <Button type="submit" aria-disabled={btnSubmitDisabled}>
            Continue
          </Button>
        </div>
      </form>
    );
  }
}
